import { Button, Typography, Grid, Dialog, DialogActions, DialogTitle } from "@mui/material";
import styles from "../../content/pages/homepage.module.css";
import { Box } from "@mui/system";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { WidgetContext } from "src/contexts/WidgetContext";
import { useContext, useState } from "react";
import { getYahooAuthUrl } from "src/api/getYahooAuthUrl";
import { useAuth0 } from "@auth0/auth0-react";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { isUnsyncedPlatform } from "src/constants/unsyncedLeague";
import CloseIcon from "@mui/icons-material/Close";
import PlatformLogo from "../PlatformSelector/PlatformLogo";

function PlatformDropdown({
  expanded,
  setLoggingInTo,
  setExpanded,
  setIsAddIcon,
  setIsSpeedDialOpen,
  setLoginError,
  handlePlatformDeleteClick,
  handlePlatformSelectClick,
  onClosePlatformPicker,
}) {
  const {
    leaguePlatforms,
    setLeaguePlatforms,
    pocToken,
    // accessAuthToken,
    setMessage,
    setTeamsDataCache,
    setUserDataCache,
    userDataCache,
    teamsDataCache,
    selectedTeams,
    setSelectedTeams,
    setSelectedTeamInfo,
    setSectionData,
    setSelectedPartner,
    setLeagueIdPlatform,
    cancelPendingRequests,
    setIsYahooLoading,
    setDataLoadedFromTeamsPage,
    setIsTransitioning,
    allowedPlatforms,
  } = useContext(WidgetContext);

  const { getAccessTokenSilently } = useAuth0();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();

  // Pending platform id when user taps X — confirm before calling handlePlatformDeleteClick
  const [pendingDeletePlatformId, setPendingDeletePlatformId] = useState<string | null>(null);

  const pendingDeletePlatform = pendingDeletePlatformId
    ? leaguePlatforms.find((p) => p.id === pendingDeletePlatformId)
    : undefined;
  const pendingDeletePlatformName = pendingDeletePlatform?.name ?? pendingDeletePlatformId ?? "";

  const handleCancelDisconnectDialog = () => {
    setPendingDeletePlatformId(null);
  };

  const handleConfirmDisconnectPlatform = async () => {
    if (!pendingDeletePlatformId || !handlePlatformDeleteClick) return;
    const platformId = pendingDeletePlatformId;
    setPendingDeletePlatformId(null);
    await handlePlatformDeleteClick(platformId);
  };

  //no la elimino todavia para testear si no surge algun bug relacionado a no usar mas esta funcion
  // Function to clean the platform data if the platform is the same
  const cleanPlatformData = (platformId: string) => {
    // Clean data of the selected teams in the same platform
    setSelectedTeams({
      ...selectedTeams,
      [platformId]: null,
    });

    // Clean the teams cache for the same platform
    setTeamsDataCache({
      ...teamsDataCache,
      [platformId]: null,
    });

    // Clean the user cache for the same platform
    setUserDataCache({
      ...userDataCache,
      [platformId]: null,
    });

    // Reset the states related to the platform if it is selected
    const isCurrentPlatform = leaguePlatforms.find((p) => p.id === platformId && p.isSelected);
    if (isCurrentPlatform) {
      setSelectedTeamInfo(null);
      setSelectedPartner(null);
      setLeagueIdPlatform(null);
      // setSectionData({
      //   commonLeagueData: null,
      //   // waiverSuggestions: null,
      //   waiver: null,
      //   news: null,
      //   start: null,
      //   rosters: null,
      //   rankings: null,
      //   rundown: null,
      //   DraftAdvice: null,
      //   superFunction: null,
      // });
      setMessage("Please select a team to continue");
    }
  };

  // Function to handle the selection of a platform
  const handlePlatformClick = async (platformId) => {
    try {
      // Set the state to true to indicate that the cache is transitioning
      setIsTransitioning(true);
      // clean the login error
      setLoginError("");

      const currentUserData = userDataCache[platformId];
      console.log("Current user data:", currentUserData);

      const platform = leaguePlatforms.find((p) => p.id === platformId);

      // Already connected (or Un-Synced): select it for PlatformSelector tab — do not open login
      if (platform?.isLoggedIn || isUnsyncedPlatform(platformId)) {
        // Toggle: clicking the already-selected platform closes ONLY the Sync League grid (panel0).
        // Restore teams accordion (panel1) when this platform has leagues — do not wipe panel1 with setExpanded("").
        if (platform?.isSelected) {
          const cached = teamsDataCache?.[platform.id];
          const platformHasTeams = Array.isArray(cached)
            ? cached.length > 0
            : Boolean(cached?.leagues?.length);
          // OLD: setExpanded(""); // closed panel0 AND panel1
          setExpanded(platformHasTeams ? "panel1" : "");
          return;
        }
        if (handlePlatformSelectClick) {
          await handlePlatformSelectClick(platformId);
        }
        // Do NOT setExpanded("") here — handlePlatformSelectClick opens panel1 when hasTeams.
        // (Previously this wiped panel1 right after opening it.)
        // setExpanded("");
        return;
      }

      // Cancel all pending requests before starting the login process
      // cancelPendingRequests();

      // Clean data and states before continuing
      // setSelectedTeamInfo(null);
      // setSelectedPartner(null); // this is for clean team name (appears the select team in ui if this is not null)
      // setLeagueIdPlatform(null);
      // setDataLoadedFromTeamsPage(false);

      // Importante: Limpiar sectionData para evitar re-ejecuciones
      setSectionData({
        commonLeagueData: null,
        // waiverSuggestions: null,
        waiver: null,
        news: null,
        start: null,
        rosters: null,
        rankings: null,
        allRankings: null,
        rundown: null,
        DraftAdvice: null,
        superFunction: null,
      });

      // Clean the platform data
      // cleanPlatformData(platformId);

      //Get the Yahoo auth URL if the platform is selected
      if (platformId === "Yahoo") {
        // check if the user is already logged in to Yahoo
        const isYahooLoggedIn = leaguePlatforms.find((p) => p.id === "Yahoo")?.isLoggedIn;

        if (!isYahooLoggedIn) {
          // Full-page loader unmounts LeagueSelector when Yahoo is already selected, so the form never opens.
          const yahooAlreadySelected = Boolean(platform?.isSelected);
          if (!yahooAlreadySelected) setIsYahooLoading(true);
          // Update the platforms before navigating
          const accessAuthToken = await getSafeAccessAuth0Token();
          //guard clause to stop execution if the token is not valid (log out)
          if (!accessAuthToken) {
            setIsYahooLoading(false);
            return;
          }
          const response = await getYahooAuthUrl(accessAuthToken);
          console.log("Yahoo Auth URL received:", response);
          //const yahooCallbackResponse = await yahooCallback(accessAuthToken, token);
          //console.log("Yahoo Callback Response:", yahooCallbackResponse);
          if (!response?.authUrl) {
            setLoginError("No valid Yahoo auth URL received, please try again");
            throw new Error("No valid Yahoo auth URL received");
          }
          // Update the platforms before navigating
          const newPlatforms = leaguePlatforms.map((platform) => ({
            ...platform,
            yahooAuthUrl: platform.id === "Yahoo" ? response : platform.yahooAuthUrl,
          }));
          setLeaguePlatforms(newPlatforms);
          setLoggingInTo(platformId);
          // Form is ready. If Yahoo is already selected, leaving this true hides the whole page (and the form).
          setIsYahooLoading(false);
          return; // Important: stop the execution here
        } else {
          // if the user is already logged in to Yahoo, simply close the dialog
          // setIsSpeedDialOpen(false);
          setLoggingInTo("Yahoo");
          setExpanded("");
          return;
        }
      }

      if (platformId === "Flea_flicker") {
        setLoggingInTo(platformId);
        setExpanded("");
      } else {
        setLoggingInTo(platformId);
      }
      setExpanded("");
    } catch (error) {
      console.error("Error getting Yahoo URL:", error);
      setMessage("Error connecting to Yahoo. Please try again.");
      setIsYahooLoading(false);
    } finally {
      // Set the state to false to indicate that the cache is not transitioning
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100); // little delay to avoid race condition
    }
  };

  // Platforms shown in the Sync League grid (Un-Synced included as a selectable card)
  const gridPlatforms =
    allowedPlatforms &&
    leaguePlatforms.filter(
      (platform) => isUnsyncedPlatform(platform.id) || allowedPlatforms.includes(platform.id),
    );

  return (
    <>
      {/* NEW: mini-card grid (2 cols mobile / 3 desktop) — same height as AnimatedButton agents */}
      <Box
        sx={{
          bgcolor: "#000000",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
          px: 0,
          // pt lives on the sticky close bar so scroll does not change X vertical spacing
          pt: 0,
          pb: 1.5,
        }}
      >
        {onClosePlatformPicker && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              flexShrink: 0,
              boxSizing: "border-box",
              height: 60,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              bgcolor: "#000000",
              // Cover subpixel gap above sticky bar while scrolling (esp. mobile WebKit)
              "&::before": {
                content: '""',
                position: "absolute",
                top: -4,
                left: 0,
                right: 0,
                height: 4,
                bgcolor: "#000000",
              },
            }}
          >
            <Button
              variant="text"
              aria-label="close platform picker"
              onClick={onClosePlatformPicker}
              sx={{
                // Match LeagueSelector teams-header X (same 40px slot, 5px from the right edge)
                p: 0,
                minWidth: "30px",
                mr: "0px",
                color: "#bbbbbb",
                // "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
        )}
        {/* Match SectionHomePage agents grid gaps so card widths align (xs≈222) */}
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} pt={0.5}>
          {gridPlatforms &&
            gridPlatforms.map((platform) => {
              const isConnected = Boolean(platform.isLoggedIn);
              const showDisconnect = isConnected && !isUnsyncedPlatform(platform.id);

              return (
                <Grid item key={platform.id} xs={6} sm={4} md={4} zeroMinWidth>
                  <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    data-platform-pick={platform.id}
                    onClick={() => handlePlatformClick(platform.id)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      height: "70px",
                      borderRadius: "8px",
                      p: 0,
                      textTransform: "none",
                      // Flat card: kill theme containedPrimary border (bottom bevel) — keep only left status strip
                      border: "none !important",
                      borderLeft: `10px solid ${isConnected ? "#FF672D" : "#555555"} !important`,
                      backgroundColor: "#222222",
                      // background: "linear-gradient(180deg, #555555, #111111)",
                      // backgroundImage: "linear-gradient(180deg, #555555, #111111)",
                      boxShadow: "none !important",
                      "&:hover": {
                        border: "none !important",
                        borderLeft: `10px solid ${isConnected ? "#FF672D" : "#555555"} !important`,
                        backgroundColor: "#222222",
                        boxShadow: "none !important",
                      },
                      "&:active, &.Mui-focusVisible": {
                        border: "none !important",
                        borderLeft: `10px solid ${isConnected ? "#FF672D" : "#555555"} !important`,
                        boxShadow: "none !important",
                      },
                    }}
                  >
                    {/* Disconnect X — only for connected real platforms (not Un-Synced) */}
                    {showDisconnect && (
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDeletePlatformId(platform.id);
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "#666666",
                          borderRadius: "50%",
                          width: 18,
                          height: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "12px", color: "#ffffff" }} />
                      </Box>
                    )}

                    {/* show logos instead of name but if is unsynce, show the name */}
                    {isUnsyncedPlatform(platform.id) ? (
                      <Typography
                        variant="h3"
                        sx={{
                          color: "#ffffff",
                          fontWeight: 600,
                          fontSize: "20px",
                          // px: 1,
                          // visibility: "hidden",
                          width: "130px",
                        }}
                      >
                        {platform.name}
                      </Typography>
                    ) : (
                      <>
                        <PlatformLogo
                          platformId={platform.id}
                          platformName={platform.name}
                          img={platform.img}
                          variant="dropdown"
                        />
                      </>
                    )}

                    {/* <Typography
                      variant="body2"
                      sx={{
                        color: "#ffffff",
                        fontWeight: 600,
                        fontSize: "14px",
                        px: 1,
                       // visibility: "hidden",
                      }}
                    >
                      {platform.name}
                    </Typography> */}
                  </Button>
                </Grid>
              );
            })}
        </Grid>
      </Box>

      {/* Confirm disconnect (moved from PlatformSelector X flow) */}
      <Dialog
        open={pendingDeletePlatformId !== null}
        onClose={handleCancelDisconnectDialog}
        maxWidth="xs"
        fullWidth
        sx={{
          zIndex: 1600,
        }}
        PaperProps={{
          sx: {
            bgcolor: "#EDEDED",
            borderRadius: 1,
            border: "0.5px solid #000000",
            boxShadow: "4px 4px 12px rgba(0,0,0,0.25)",
            p: 0.5,
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              position: "fixed",
              backdropFilter: "blur(1px) !important",
              WebkitBackdropFilter: "blur(1px) !important",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
            },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: PureLightThemeCaaS.colors.alpha.black[100], fontWeight: 700, p: 1, pb: 0 }}>
          Are you sure you want to disconnect {pendingDeletePlatformName}?
        </DialogTitle>
        <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleCancelDisconnectDialog}
            autoFocus
            sx={{
              flex: 1,
              whiteSpace: "nowrap",
              p: 0.5,
              bgcolor: PureLightThemeCaaS.colors.alpha.orange[100],
              "&:hover": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[90] },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => void handleConfirmDisconnectPlatform()}
            sx={{
              flex: 1,
              whiteSpace: "nowrap",
              p: 0.5,
              borderColor: PureLightThemeCaaS.colors.alpha.black[70],
              color: PureLightThemeCaaS.colors.alpha.black[100],
              "&:hover": { borderColor: PureLightThemeCaaS.colors.alpha.black[100] },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* OLD: vertical list design (kept for easy rollback) — restore by uncommenting this block and removing the grid above
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" bgcolor="#000000" boxShadow="0px 4px 8px rgba(0, 0, 0, 0.2)">
        {allowedPlatforms &&
          leaguePlatforms
            .filter((platform) => allowedPlatforms.includes(platform.id) && !isUnsyncedPlatform(platform.id))
            .map((platform) => (
            <Button
              key={platform.id}
              fullWidth
              variant="outlined"
              color="info"
              sx={{
                width: "90%",
                "&:first-of-type": { borderTop: "0px solid #ffffff !important" },
                "&:last-child": {
                  borderBottomLeftRadius: "10px !important",
                  borderBottomRightRadius: "10px !important",
                  borderBottom: "0px solid #ffffff !important",
                },
              }}
              className={styles.buttonLeagueName}
              onClick={() => handlePlatformClick(platform.id)}
            >
              <Box display="flex" alignItems="center" justifyContent="flex-start">
                <Typography variant="body2" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} sx={{ pl: 0.5, p: 0.5 }}>
                  {platform.name}
                </Typography>
              </Box>
            </Button>
          ))}
      </Box>
      */}
    </>
  );
}

export default PlatformDropdown;
